import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { COLOURS } from "../theme";

const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(3px);
  z-index: 100;
`;

const Panel = styled(motion.aside)`
  position: fixed;
  right: 0;
  top: 0;
  height: 100vh;
  width: min(420px, 90vw);
  background: linear-gradient(
    to bottom,
    ${COLOURS.bg},
    "rgba(255,255,255,0.96)"}
  );
  border-left: 1px solid ${COLOURS.textMuted};
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.18);
  z-index: 101;
  backdrop-filter: blur(4px);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px 32px;
  border-bottom: 1px solid ${COLOURS.textMuted};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
`;

const Title = styled.h2`
  font-size: 26px;
  font-weight: 600;
  color: ${COLOURS.textStrong};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${COLOURS.text};
  font-size: 26px;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: color 0.2s ease, background 0.2s ease, transform 0.15s ease;

  &:hover,
  &:focus-visible {
    color: ${COLOURS.accent};
    background: rgba(0, 0, 0, 0.05);
    transform: scale(1.05);
    outline: none;
  }
`;

const Content = styled.div`
  padding: 28px 32px;
  overflow-y: auto;
`;

const Body = styled.p`
  font-size: 18px;
  line-height: 1.75;
  color: ${COLOURS.text};
  margin-bottom: 26px;
  padding-left: 18px;
  border-left: 2px solid ${COLOURS.textMuted};
  letter-spacing: 0.15px;
`;

type Props = {
  open: boolean;
  onClose: () => void;
};

const AboutPanel: React.FC<Props> = ({ open, onClose }) => {
  return (
    <AnimatePresence mode="wait">
      {open && (
        <>
          <Backdrop
            key="backdrop"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          />

          <Panel
            key="panel"
            initial={{ x: "100%" }}
            animate={{
              x: 0,
              transition: {
                type: "spring",
                stiffness: 240,
                damping: 28,
              },
            }}
            exit={{
              x: "100%",
              transition: {
                type: "spring",
                stiffness: 240,
                damping: 28,
                mass: 1,
              },
            }}
          >
            <Header>
              <Title>About Me</Title>
              <CloseButton aria-label="Close panel" onClick={onClose}>
                <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
              </CloseButton>
            </Header>

            <Content>
              <Body>
                ✦ I’m Amy, a front‑end software engineer with 4 years of
                experience building web portals. I love exploring new tools and
                techniques that help me write cleaner, more thoughtful code.
              </Body>

              <Body>
                ✦ Outside of work, you’ll usually find me hiking, swimming, or
                running Girlguiding activities. I care deeply about
                accessibility, design systems, and creating digital experiences
                that feel warm and human.
              </Body>

              <Body>
                ✦ Long‑term, I want to keep growing as an engineer, explore more
                product‑focused roles, and build tools that genuinely help
                people.
              </Body>
            </Content>
          </Panel>
        </>
      )}
    </AnimatePresence>
  );
};

export default AboutPanel;
